import { type WorkspaceText, workspaceTextFor } from '../interface-copy';
import { useWorkspaceText } from '../interface-copy';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { companyRequest } from '@/company/api';
import { useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/connection-access';
import { useConnectionPeople } from './policy-fields';
import { listConnectionReadAccess, type ConnectionReadItem } from '../connection-read-access/api';
import type { ConnectionAuditPage } from '../../../src/connectors/read-access/index.mjs';
const kinds = (uiText: WorkspaceText): Record<string, string> => ({
  'connection.created': uiText('Connection created'),
  'connection.shared': uiText('Sharing changed'),
  'connection.user.granted': uiText('Person added'),
  'connection.user.removed': uiText('Person removed'),
  'connection.access.changed': uiText('Access changed'),
  'connection.revoked': uiText('Access turned off'),
  'connection.removed': uiText('Connection removed'),
  'connection.settings.changed': uiText('Query time limit changed'),
});

/** A filterable event list, backed by the same company audit. No dashboard or client authority. */
export function ConnectionActivity() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(copyCatalog);
  const people = useConnectionPeople();
  const [connections, setConnections] = useState<ConnectionReadItem[]>([]);
  const [query, setQuery] = useState({
    person: '',
    connection: '',
    app: '',
    from: '',
    until: '',
    result: '',
  });
  const [page, setPage] = useState<ConnectionAuditPage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [request, setRequest] = useState<URLSearchParams>(new URLSearchParams());
  useEffect(() => {
    void listConnectionReadAccess()
      .then((value) => setConnections(value.items))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    let active = true;
    setBusy(true);
    setError(false);
    void companyRequest<ConnectionAuditPage>(`/api/v1/company/connection-activity?${request}`)
      .then((value) => {
        if (active) setPage(value);
      })
      .catch(() => {
        if (active) {
          setError(true);
          setPage(null);
        }
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [request]);
  function search() {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query))
      if (value)
        params.set(key, key === 'from' || key === 'until' ? new Date(value).toISOString() : value);
    setRequest(params);
  }
  return (
    <section className="ws-connection-activity" aria-labelledby="connection-activity-title">
      <h2 id="connection-activity-title">{copy.activityTitle}</h2>
      <form
        className="ws-connection-activity-filters"
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
      >
        <label>
          {uiText('Person')}
          <select
            value={query.person}
            onChange={(event) => setQuery({ ...query, person: event.target.value })}
          >
            <option value="">{uiText('Everyone')}</option>
            {people.map((person) => (
              <option key={person.userId} value={person.userId}>
                {person.displayName || person.email}
              </option>
            ))}
          </select>
        </label>
        <label>
          {uiText('Connection')}
          <select
            value={query.connection}
            onChange={(event) => setQuery({ ...query, connection: event.target.value })}
          >
            <option value="">{uiText('All connections')}</option>
            {connections.map((item) => (
              <option key={item.connectionId} value={item.connectionId}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {uiRich('App {value1}', {
            value1: (
              <input
                type="text"
                value={query.app}
                onChange={(event) => setQuery({ ...query, app: event.target.value })}
                placeholder={uiText('App reference')}
              />
            ),
          })}
        </label>
        <label>
          {uiRich('From {value1}', {
            value1: (
              <input
                type="datetime-local"
                value={query.from}
                onChange={(event) => setQuery({ ...query, from: event.target.value })}
              />
            ),
          })}
        </label>
        <label>
          {uiRich('Until {value1}', {
            value1: (
              <input
                type="datetime-local"
                value={query.until}
                onChange={(event) => setQuery({ ...query, until: event.target.value })}
              />
            ),
          })}
        </label>
        <label>
          {uiText('Result')}
          <select
            value={query.result}
            onChange={(event) => setQuery({ ...query, result: event.target.value })}
          >
            <option value="">{uiText('All results')}</option>
            <option value="succeeded">{uiText('Succeeded')}</option>
            <option value="denied">{uiText('Denied')}</option>
            <option value="failed">{uiText('Failed')}</option>
            <option value="unknown">{uiText('Unknown')}</option>
          </select>
        </label>
        <Button type="submit" disabled={busy}>
          {uiText('Filter')}
        </Button>
      </form>
      {busy && <p role="status">{uiText('Loading activity…')}</p>}
      {error && <p role="alert">{uiText('Could not load activity. Try again.')}</p>}
      {page && !page.items.length && <p>{copy.activityEmpty}</p>}
      <ol className="ws-connection-activity-list">
        {page?.items.map((event) => (
          <li key={event.id}>
            <time dateTime={event.occurredAt}>
              {new Date(event.occurredAt).toLocaleString(uiText.locale)}
            </time>
            <strong>
              {people.find((person) => person.userId === event.userId)?.displayName || event.userId}
            </strong>
            <span>
              {connections.find((item) => item.connectionId === event.connection.connectionId)
                ?.name || uiText('Removed connection')}
            </span>
            <span>
              {event.eventKind === 'connection.call'
                ? `${event.connection.effect === 'read' ? uiText('Read') : uiText('Write')} · ${event.connection.tool}`
                : kinds(uiText)[event.eventKind]}
            </span>
            <span>
              {event.connection.decision === 'denied'
                ? uiText('Denied')
                : event.connection.result === 'succeeded'
                  ? uiText('Succeeded')
                  : event.connection.result === 'failed'
                    ? uiText('Failed')
                    : uiText('Unknown')}
            </span>
            {event.appId && <small>{uiRich('App: {value1}', { value1: event.appId })}</small>}
          </li>
        ))}
      </ol>
      {page?.next && (
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            const params = new URLSearchParams(request);
            params.set('before', page.next!.before);
            params.set('beforeId', page.next!.beforeId);
            setRequest(params);
          }}
        >
          {uiText('Older activity')}
        </Button>
      )}
    </section>
  );
}
